import React, { useEffect } from 'react';
import { CgSpinner } from 'react-icons/cg';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BankAccount, GoCardlessAccount } from '../../../types/bankAccount';
import PageHeader from '../../components/misc/PageHeader';
import * as bankAccountService from '../../../services/bankAccountService';
import Button from '../../components/buttons/Button';

export default function CompleteCardlessPage() {
  const { t } = useTranslation('', { keyPrefix: 'page.financial.completeCardless' });
  const [accounts, setAccounts] = React.useState<GoCardlessAccount[] | null>();
  const [veraBankAccounts, setVeraBankAccounts] = React.useState<BankAccount[] | null>();
  const [matchedAccount, setMatchedAccount] = React.useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requisitionId = searchParams.get('ref');

  useEffect(() => {
    // TODO
    bankAccountService
      .getAllBankAccounts()
      .then(setVeraBankAccounts)
      .catch(() => setVeraBankAccounts(null));

    if (!requisitionId) {
      setAccounts(null);
      return;
    }

    bankAccountService
      .getAccountsByRequisition(requisitionId)
      .then(setAccounts)
      .catch(() => setAccounts(null));
  }, []);

  useEffect(() => {
    if (accounts && veraBankAccounts && requisitionId) {
      const veraAccount = veraBankAccounts.find((ba) => ba.requisitionId === requisitionId);
      const goCardlessAccount = accounts.find((gca) => gca.iban === veraAccount?.iban);

      if (goCardlessAccount && veraAccount) {
        setMatchedAccount(goCardlessAccount.iban);
        buildTransactionsForAccount(goCardlessAccount.id, veraAccount.accountId);
      } else {
        setMatchedAccount(null);
      }
    }
  }, [accounts, veraBankAccounts]);

  const buildTransactionsForAccount = (goCardlessAccountId: string, veraAccountId: number) => {
    console.log('buildTransactionsForAccount', goCardlessAccountId, veraAccountId);
    bankAccountService.createGoCardlessTransactions(goCardlessAccountId, veraAccountId).then(() => {
      navigate(`/financial/mutations/${veraAccountId}`);
    });
  };

  if (!requisitionId) {
    return (
      <>
        <PageHeader
          title={t('error.noRequisitionTitle')}
          subtitle={t('error.noRequisitionSubtitle')}
        />
        <Link to="/financial/overview">
          <Button variant="primary-dark">{t('toAccountEntries')}</Button>
        </Link>
      </>
    );
  }

  return (
    <>
      <PageHeader title={t('title')} />
      <div className="flex flex-col items-center justify-center gap-2">
        {accounts === undefined && veraBankAccounts === undefined && (
          <CgSpinner className="animate-spin" size={120} />
        )}
        {(accounts === null || veraBankAccounts === null) && (
          <p className="text-xl text-danger">{t('error.general')}</p>
        )}
        {veraBankAccounts &&
          accounts &&
          (matchedAccount ? (
            <p className="text-xl">{t('linking', { accountNumber: matchedAccount })}</p>
          ) : (
            <>
              <p className="text-xl">
                {t('error.noMatch', {
                  accountNumber:
                    veraBankAccounts.find((ba) => ba.requisitionId === requisitionId)?.iban ??
                    t('thisAccount'),
                })}
              </p>
              <Link to="/financial/overview">
                <Button variant="primary-dark">{t('toAccountEntries')}</Button>
              </Link>
            </>
          ))}
      </div>
    </>
  );
}
